import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useMyNotifications, useMarkNotificationsAsRead } from "../hooks";
import { notificationLink } from "../notification-routes";
import { formatDisplayDate } from "../../../helpers/date";

const PREVIEW_LIMIT = 10;

export const NotificationsBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpened, setIsOpened] = useState(false);
  const { data } = useMyNotifications({ where: { limit: PREVIEW_LIMIT } });
  const { markAsRead, markAllAsRead } = useMarkNotificationsAsRead();

  const notifications = data?.myNotifications?.results ?? [];
  const unreadCount = data?.myNotifications?.unreadCount ?? 0;

  const handleClick = (notification: {
    id: number;
    read: boolean;
    table?: any;
    objectId?: number | null;
  }) => {
    if (!notification.read) {
      markAsRead([notification.id]);
    }
    const link = notificationLink(notification.table, notification.objectId);
    if (link) {
      setIsOpened(false);
      navigate(link);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpened(!isOpened)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
        title="Notifications"
      >
        <Bell size={20} className="text-gray-600 dark:text-zinc-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpened && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpened(false)} />
          <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-surface-dark-alt rounded-lg shadow-lg border border-gray-200 dark:border-zinc-800 z-40 max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-zinc-800">
              <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                Notifications
              </p>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllAsRead()}
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                >
                  <CheckCheck size={13} />
                  Tout marquer comme lu
                </button>
              )}
            </div>

            <div className="overflow-y-auto">
              {!notifications.length ? (
                <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-zinc-400">
                  Aucune notification
                </p>
              ) : (
                notifications.map((notification) => {
                  const link = notificationLink(
                    notification.table,
                    notification.objectId
                  );
                  return (
                    <div
                      key={`notification-${notification.id}`}
                      onClick={() => handleClick(notification)}
                      className={`px-4 py-3 border-b border-gray-50 dark:border-zinc-800/60 last:border-0 ${
                        link ? "cursor-pointer" : ""
                      } ${
                        notification.read
                          ? "hover:bg-gray-50 dark:hover:bg-zinc-800/50"
                          : "bg-indigo-50/60 dark:bg-indigo-950/20 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!notification.read && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm truncate ${
                              notification.read
                                ? "text-gray-700 dark:text-zinc-300"
                                : "font-semibold text-gray-900 dark:text-zinc-100"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-zinc-400 line-clamp-2">
                            {notification.content}
                          </p>
                          <p className="text-[10px] text-gray-400 dark:text-zinc-500 mt-0.5">
                            {formatDisplayDate(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="px-4 py-2 border-t border-gray-100 dark:border-zinc-800">
              <button
                type="button"
                onClick={() => {
                  setIsOpened(false);
                  navigate("/notifications");
                }}
                className="w-full text-xs text-indigo-600 hover:underline"
              >
                Voir toutes les notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
